import React, { useState, useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Drawer,
  MenuItem,
  List,
  Typography,
  Button,
  Box,
  Grid,
  Divider,
  Menu,
  Modal,
  ListItemIcon
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CircleIcon from '@mui/icons-material/Circle';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Tooltip } from 'pepsico-ds';
import {
  setSelectedCycleId,
  setSelectedCycle,
  setBusinessUnit
} from '../../redux/HomePage/cyclesByBuSlice';
import textIcon from '../../assests/images/textIcon.png';
import bookmarkIcon from '../../assests/images/bookmark.svg';
import helpIcon from '../../assests/images/help_icon.svg';
import logo from '../../assests/images/logo.png';
import { useOkta } from '../../hooks/oktaHook';
import { changeReadFlag, fetchNotifications, fetchUserDetail } from '../../pages/HomePage/service';
import { setUserInfo } from '../../redux/General/generalSlice';
import EditProfileModal from '../../pages/SetupTeam/Profile/EditProfileModal';
import { CORE_USER, TPO_ADMIN } from '../../utils/Constants';

const HEARTBEAT_DELAY_IN_MS = 3 * 60 * 1000; // 3 Minutes

export default function Header() {
  const { oktaAuth, userDetails } = useOkta();
  const dispatch = useDispatch();
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);
  const isExistingUser = useSelector((state) => state.general.isExistingUser);
  const settings = [
    {
      icon: <AccountCircleOutlinedIcon />,
      name: userDetails.username
    },
    {
      icon: <LoginOutlinedIcon />,
      name: 'Log Out'
    }
  ];

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [profileInfo, setProfileInfo] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [user_id, setUser_id] = useState('');
  const [loading, setLoading] = useState({
    notifications: true,
    profile: true
  });
  const [error, setError] = useState({ notifications: null, profile: null });

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  useEffect(() => {
    const fetchUserInfoAndNotifications = async () => {
      setLoading(true);
      setError(null);
      try {
        const userInfoResponse = await fetchUserDetail(userDetails?.email, selectedBu);

        setProfileInfo(userInfoResponse);

        const { userId } = userInfoResponse;
        setUser_id(userId || userDetails?.gpid);
        dispatch(setUserInfo(userInfoResponse));

        if (userId && isExistingUser) {
          const notificationsResponse = await fetchNotifications(userId, selectedBu);
          setNotifications(notificationsResponse);
        }
      } catch (err) {
        setError('Error fetching user info or notifications.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (userDetails?.email && selectedBu && isExistingUser) {
      fetchUserInfoAndNotifications();
    }
  }, [selectedBu, userDetails?.email, isExistingUser, openModal]);

  const heartbeat = async () => {
    if (!user_id || !selectedBu || !isExistingUser) {
      console.warn('Heartbeat skipped: Missing user_id or selectedBu');
      return;
    }

    try {
      const heartbeatNotificationResponse = await fetchNotifications(user_id, selectedBu);
      setNotifications(heartbeatNotificationResponse);
    } catch (error) {
      console.error('Heartbeat error:', error);
    }
  };

  useEffect(() => {
    if (user_id && selectedBu && isExistingUser) {
      const interval = setInterval(() => heartbeat(), HEARTBEAT_DELAY_IN_MS);
      return () => clearInterval(interval);
    }
  }, [user_id, selectedBu, isExistingUser]);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = (event) => {
    setAnchorElUser(null);
  };

  const handleMenuItemClick = async (item) => {
    if (item.name === 'Log Out') {
      dispatch(setBusinessUnit(''));
      localStorage.setItem('cycleId', null);
      await oktaAuth.signOut();
    }
  };
  const [hasNotification, setHasNotification] = useState(
    notifications.length ? notifications.some((notification) => !notification.readFlag) : false
  );

  const handleNotificationClick = async (
    notificationId,
    notificationCategory,
    userTaskMappingId
  ) => {
    if (!user_id || !notificationId || !notificationCategory) {
      console.warn('Cannot mark notification as read: Missing user_id or selectedBu');
      return;
    }

    try {
      const payload = {
        userId: user_id,
        taskId: notificationId.toString(),
        notificationCategory: notificationCategory,
        userTaskMappingId: userTaskMappingId
      };
      await changeReadFlag(payload);

      // Update the notifications state to reflect the change
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.taskId === notificationId
            ? { ...notification, readFlag: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  useEffect(() => {
    setHasNotification(notifications.some((notification) => !notification.readFlag));
  }, [notifications]);

  const renderWithTooltip = (items, title = '') => {
    const itemArray = Array.isArray(items) ? items : [items];

    if (!itemArray || itemArray.length === 0) {
      return null;
    }

    const isMultipleItems = itemArray.length > 1;

    return (
      <div>
        <Typography variant="body1" fontWeight={400} style={{ color: '#3A3A3A' }}>
          {title}
        </Typography>
        {isMultipleItems ? (
          <Tooltip
            style={{ color: '#ffffff' }}
            direction="bottom"
            text={
              <ul
                style={{
                  margin: 10,
                  padding: 10,
                  listStyleType: 'none',
                  backgroundColor: '#000000'
                }}>
                {itemArray.map((item, index) => (
                  <li key={`${index + 1}`}>{item}</li>
                ))}
              </ul>
            }
            arrow>
            <Typography variant="body2">
              {itemArray[0]}...<span style={{ fontWeight: 800 }}>+{itemArray.length - 1} more</span>
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" style={{ color: '#A0A0A0' }}>
            {itemArray[0]}
          </Typography>
        )}
      </div>
    );
  };

  const handleAction = (val) => {
    setOpenModal(val);
  };

  return (
    <>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: (theme) => theme.palette.grey[800]
        }}>
        <Toolbar sx={{ marginTop: '-5px' }}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item>
              <Box>
                <img src={logo} alt="Logo" />
              </Box>
            </Grid>

            <Grid item>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}>
                <Grid item>
                  <img src={textIcon} alt="icon" />
                </Grid>
                <Grid item>
                  <Typography>Portfolio Optimization</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item sx={{ flexGrow: 0 }}>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="right"
                spacing={1}>
                <Grid item>
                  <a href="https://pepsico.service-now.com/mypepsico?id=sc_cat_item&sys_id=3f1dd0320a0a0b99000a53f7604a2ef9"
                   style={{ textDecoration: 'underline', color: '#FFFFFF', position: 'relative', top: '7px', right: '22px', cursor: 'pointer' }}
                   target="_blank"
                   rel="noopener noreferrer">
                    HELP
                  </a>
                </Grid>
                <Grid item>
                  <a href="/info" rel="noopener noreferrer">
                    <img
                      src={helpIcon}
                      alt="icon"
                      style={{ position: 'relative', top: '6px', right: '20px', cursor: 'pointer' }}
                    />
                  </a>
                </Grid>
                <Grid item>
                  <a
                    href="https://pepsico.sharepoint.com/:p:/t/TPO-Gen3/ET9TCC-0SpNBjHFYQMFjU44BrXsQrl8LFTwZbPFEF0_95A?e=wo4aTt"
                    target="_blank"
                    rel="noopener noreferrer">
                    <img
                      src={bookmarkIcon}
                      alt="icon"
                      style={{ position: 'relative', top: '6px', right: '10px', cursor: 'pointer' }}
                    />
                  </a>
                </Grid>
                <Grid item onClick={handleNotificationClick}>
                  <Badge color="error" variant="dot" invisible={!hasNotification}>
                    <AccountCircleIcon
                      data-testid="profile-icon"
                      sx={{
                        xs: 'none',
                        md: 'flex',
                        color: '#FFFFFF',
                        width: ' 40px',
                        height: '40px'
                      }}
                      onClick={() => setDrawerOpen(true)}
                    />
                  </Badge>
                </Grid>

                <Grid item>
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <ArrowDropDownIcon
                      fontSize="10px"
                      sx={{
                        xs: 'none',
                        md: 'flex',
                        color: '#FFFFFF',
                        marginTop: '.7rem'
                      }}
                    />
                  </IconButton>

                  <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={toggleDrawer(false)}
                    PaperProps={{
                      sx: {
                        width: { xs: '100%', sm: 500 },
                        marginTop: '50px',
                        height: 'calc(100% - 50px)',
                        display: 'flex',
                        flexDirection: 'column'
                      }
                    }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                      <Typography variant="h6" fontWeight={700} style={{ color: '#3A3A3A' }}>
                        Profile
                      </Typography>
                      <IconButton onClick={toggleDrawer(false)}>
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                      <List sx={{ width: '100%' }}>
                        <Grid container>
                          <Grid item xs={6}>
                            <Typography
                              variant="h5"
                              sx={{ padding: 2, fontWeight: 700 }}
                              style={{ color: '#000000' }}>
                              Profile Information
                            </Typography>
                          </Grid>
                          {!userDetails?.roles.some((role) =>
                            [CORE_USER, TPO_ADMIN].includes(role)
                          ) && (
                              <>
                                <Grid item xs={6} style={{ position: 'relative', top: '20px' }}>
                                  <Icon
                                    alt="alt value"
                                    className="$color-icon-default"
                                    icon="edit"
                                    size="medium"
                                    onClick={() => setOpenModal(true)}
                                    data-testid="edit-icon"
                                  />
                                </Grid>
                              </>
                            )}
                        </Grid>

                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} key={profileInfo.id}>
                              <Grid container spacing={3}>
                                <Grid item xs={6}>
                                  {renderWithTooltip(userDetails?.username, 'Name')}
                                </Grid>
                                {/* <Typography variant="body2">{userDetails?.username}</Typography>
                                </Grid> */}
                                <Grid item xs={6}>
                                  {renderWithTooltip(userDetails?.email, 'Email')}
                                </Grid>
                                <Grid item xs={6}>
                                  {renderWithTooltip(userDetails?.roles, 'User Type')}
                                </Grid>
                                <Grid item xs={6}>
                                  {renderWithTooltip(userDetails?.BUs, 'BU Name')}
                                </Grid>
                                {profileInfo?.functionName == null ||
                                  profileInfo?.functionValue == null ? null : (
                                  <>
                                    <Grid item xs={6}>
                                      {renderWithTooltip(profileInfo?.functionName, 'Function')}
                                    </Grid>
                                    <Grid item xs={6}>
                                      {renderWithTooltip(
                                        profileInfo?.functionValue,
                                        ['Customer', 'Region', 'Channel'].includes(
                                          profileInfo?.functionName
                                        )
                                          ? profileInfo?.functionName
                                          : 'Function Detail'
                                      )}
                                    </Grid>
                                  </>
                                )}
                              </Grid>
                            </Grid>

                            {/* ))} */}
                          </Grid>
                        </Box>

                        <Typography variant="h5" sx={{ padding: 2, fontWeight: 700 }}>
                          Notifications
                        </Typography>

                        {loading ? (
                          <Typography variant="body1" sx={{ padding: 2 }}>
                            Loading notifications...
                          </Typography>
                        ) : error?.notifications ? (
                          <Typography variant="body1" color="error" sx={{ padding: 2 }}>
                            {error?.notifications}
                          </Typography>
                        ) : notifications.length ? (
                          [...notifications]
                            .sort((a, b) => b.createdOn - a.createdOn)
                            .map((notification) => {
                              const now = Math.floor(Date.now() / 1000);
                              const diffInSeconds = now - notification.createdOn;
                              const timeAgo =
                                diffInSeconds < 60
                                  ? `${diffInSeconds} seconds ago`
                                  : diffInSeconds < 3600
                                    ? `${Math.floor(diffInSeconds / 60)} minutes ago`
                                    : diffInSeconds < 86400
                                      ? `${Math.floor(diffInSeconds / 3600)} hours ago`
                                      : `${Math.floor(diffInSeconds / 86400)} days ago`;

                              return (
                                <React.Fragment key={notification.id}>
                                  <Box
                                    display="flex"
                                    alignItems="start"
                                    p={2}
                                    onClick={() =>
                                      handleNotificationClick(
                                        notification?.taskId,
                                        notification?.notificationCategory,
                                        notification?.userTaskMappingId
                                      )
                                    }
                                    style={{ cursor: 'pointer' }}>
                                    {!notification.readFlag ? (
                                      <CircleIcon
                                        fontSize="extraSmall"
                                        sx={{ mr: 1 }}
                                        style={{ color: '#D40020' }}
                                      />
                                    ) : (
                                      <CircleIcon
                                        fontSize="extraSmall"
                                        sx={{ mr: 1 }}
                                        style={{ visibility: 'hidden' }}
                                      />
                                    )}
                                    <Box>
                                      <Typography variant="body1" fontWeight={600}>
                                        <Link
                                          to={`${notification.url}?bu=${selectedBu}&cycleName=${notification?.cycleName}&cycleId=${notification?.cycleId}`}
                                          style={{ textDecoration: 'none', color: 'inherit' }}
                                          onClick={() => {
                                            dispatch(setSelectedCycleId(notification?.cycleId));
                                            dispatch(setSelectedCycle(notification?.cycleName));
                                          }}>
                                          <Box display="flex" justifyContent="space-between">
                                            <span style={{ color: '#3A3A3A' }}>
                                              {notification.notificationName}
                                            </span>
                                            <span
                                              style={{
                                                color: '#005CBC',
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                marginLeft: '1rem'
                                              }}>
                                              {notification.hyperlinkName}
                                            </span>
                                          </Box>
                                        </Link>
                                      </Typography>
                                      <div>
                                        <Typography
                                          variant="caption"
                                          color="textSecondary"
                                          style={{ color: '#3A3A3A' }}>
                                          {notification.description}
                                        </Typography>
                                      </div>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{ color: '#3A3A3A' }}>
                                        {timeAgo}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Divider />
                                </React.Fragment>
                              );
                            })
                        ) : (
                          <Box p={2}>
                            <Typography variant="h6" fontWeight={700}>
                              No notifications for now, all caught up!
                            </Typography>
                          </Box>
                        )}
                      </List>
                    </Box>

                    <Box
                      p={2}
                      sx={{ borderTop: '1px solid #ddd' }}
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="center">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={toggleDrawer(false)}
                        sx={{ borderRadius: 20 }}>
                        Close
                      </Button>
                    </Box>
                  </Drawer>

                  <Menu
                    sx={{
                      mt: '45px',

                      '& .MuiPaper-root': {
                        backgroundColor: '#272727', // Black background for the Menu
                        boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.08)',
                        padding: '8px 16px',
                        width: '300px',
                        overflow: 'auto',
                        borderRadius: '8px'
                      }
                    }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}>
                    {settings.map((setting, i) => {
                      const listStyle = { color: '#EFEFEF' };
                      if (i == 0) {
                        listStyle.width = '1.5rem';
                      }
                      return (
                        <MenuItem
                          key={setting.name}
                          onClick={() => handleMenuItemClick(setting)}
                          sx={{
                            backgroundColor: '#1D1D1D',
                            padding: '8px',
                            borderBottom: '0px !important',
                            boxShadow: 'inset 0px -1px 0px #3A3A3A',
                            color: '#EFEFEF' // White text for the Menu
                          }}>
                          <ListItemIcon sx={listStyle}>{setting.icon}</ListItemIcon>
                          <Typography variant="body1" fontWeight="700">
                            {setting.name}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {openModal ? (
        <Modal
          style={{
            // height:'600px', width:'800px', backgroundColor:'#FBFCFE',
            margin: '0 0',
            top: '10%',
            left: '10%'
          }}
          open={openModal}
          // showModal={openModal}
          // closeOnOutsideClick
          // onCloseModal={()=> setOpenModal(false)}
          onClose={() => setOpenModal(false)}
        // type="M"
        // // onClose={handleClose}
        // showTertiaryButton={false}
        // showPrimaryButton={false}
        // showSecondaryButton={false}
        // showAuxiliaryButton={false}
        // showLink={false}
        // style={{
        //     height: '100%',
        //     width: '100%'
        // }}
        >
          <EditProfileModal userDetails={userDetails} handleAction={handleAction} />
        </Modal>
      ) : (
        <></>
      )}
    </>
  );
}
